import React from 'react';
import './page-loader.css';

const STARS: { cx: number; cy: number; r: number; delay: string; duration: string }[] = [
  { cx: 40, cy: 30, r: 1.5, delay: '0s', duration: '2.1s' },
  { cx: 90, cy: 55, r: 1, delay: '0.4s', duration: '1.8s' },
  { cx: 140, cy: 20, r: 2, delay: '0.8s', duration: '2.5s' },
  { cx: 220, cy: 35, r: 1, delay: '0.2s', duration: '1.6s' },
  { cx: 310, cy: 25, r: 1.5, delay: '1s', duration: '2.2s' },
  { cx: 360, cy: 60, r: 1, delay: '0.6s', duration: '1.9s' },
  { cx: 50, cy: 200, r: 1.5, delay: '1.2s', duration: '2.4s' },
  { cx: 380, cy: 190, r: 1, delay: '0.3s', duration: '1.7s' },
  { cx: 170, cy: 260, r: 2, delay: '0.9s', duration: '2s' },
  { cx: 280, cy: 255, r: 1.5, delay: '0.5s', duration: '2.3s' },
  { cx: 330, cy: 240, r: 1, delay: '1.4s', duration: '1.5s' },
  { cx: 70, cy: 240, r: 1, delay: '0.7s', duration: '2.1s' },
];

const STREAKS: { x1: number; y1: number; x2: number; y2: number; delay: string }[] = [
  { x1: 200, y1: 140, x2: 60, y2: 80, delay: '0s' },
  { x1: 200, y1: 140, x2: 340, y2: 80, delay: '0.3s' },
  { x1: 200, y1: 140, x2: 60, y2: 200, delay: '0.6s' },
  { x1: 200, y1: 140, x2: 340, y2: 200, delay: '0.9s' },
  { x1: 200, y1: 140, x2: 200, y2: 30, delay: '0.15s' },
  { x1: 200, y1: 140, x2: 200, y2: 250, delay: '0.45s' },
  { x1: 200, y1: 140, x2: 100, y2: 45, delay: '0.75s' },
  { x1: 200, y1: 140, x2: 300, y2: 45, delay: '1.05s' },
];

export const PageLoader: React.FC = () => (
  <div
    style={{
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'var(--cb-bg-primary)',
      gap: 24,
    }}
  >
    <svg
      aria-hidden="true"
      viewBox="0 0 400 280"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      style={{ width: '100%', maxWidth: 320 }}
    >
      {/* ── Twinkling stars ── */}
      {STARS.map((s, i) => (
        <circle
          key={i}
          cx={s.cx}
          cy={s.cy}
          r={s.r}
          fill="var(--cb-gold)"
          className="page-loader-star"
          style={{ animation: `twinkle ${s.duration} ease-in-out ${s.delay} infinite` }}
        />
      ))}

      {/* ── Hyperspace streaks ── */}
      {STREAKS.map((s, i) => (
        <line
          key={i}
          x1={s.x1}
          y1={s.y1}
          x2={s.x2}
          y2={s.y2}
          stroke="var(--cb-accent)"
          strokeWidth="1"
          strokeLinecap="round"
          opacity="0.4"
          className="page-loader-streak"
          style={{
            transformOrigin: `${s.x1}px ${s.y1}px`,
            animation: `streak 2s ease-out ${s.delay} infinite`,
          }}
        />
      ))}

      {/* ── Outer orbit ring ── */}
      <ellipse
        cx="200"
        cy="140"
        rx="108"
        ry="28"
        stroke="var(--cb-border)"
        strokeWidth="1"
        strokeDasharray="6 4"
        className="page-loader-orbit-3"
        style={{ animation: 'orbit 18s linear infinite', transformOrigin: '200px 140px' }}
      />

      {/* ── Mid orbit ring ── */}
      <ellipse
        cx="200"
        cy="140"
        rx="80"
        ry="20"
        stroke="var(--cb-accent)"
        strokeWidth="1"
        strokeDasharray="4 6"
        opacity="0.5"
        className="page-loader-orbit-2"
        style={{ animation: 'orbitReverse 10s linear infinite', transformOrigin: '200px 140px' }}
      />

      {/* ── Inner orbit ring ── */}
      <ellipse
        cx="200"
        cy="140"
        rx="56"
        ry="14"
        stroke="var(--cb-gold)"
        strokeWidth="1.5"
        strokeDasharray="3 5"
        opacity="0.7"
        className="page-loader-orbit-1"
        style={{ animation: 'orbit 6s linear infinite', transformOrigin: '200px 140px' }}
      />

      {/* ── Outer orbit dot ── */}
      <circle
        cx="200"
        cy="140"
        r="5"
        fill="var(--cb-border)"
        className="page-loader-dot-3"
        style={{ animation: 'orbitDotOuter 18s linear infinite', transformOrigin: '200px 140px' }}
      />

      {/* ── Mid orbit dot ── */}
      <circle
        cx="200"
        cy="140"
        r="4"
        fill="var(--cb-accent)"
        className="page-loader-dot-2"
        style={{
          animation: 'orbitDotReverse 10s linear infinite',
          transformOrigin: '200px 140px',
        }}
      />

      {/* ── Inner orbit dot ── */}
      <circle
        cx="200"
        cy="140"
        r="5"
        fill="var(--cb-gold)"
        className="page-loader-dot-1"
        style={{ animation: 'orbitDot 6s linear infinite', transformOrigin: '200px 140px' }}
      />

      {/* ── Core glow ── */}
      <circle
        cx="200"
        cy="140"
        r="48"
        fill="var(--cb-accent)"
        style={{ animation: 'glowPulse 2.5s ease-in-out infinite' }}
      />

      {/* ── Core planet ── */}
      <circle
        cx="200"
        cy="140"
        r="22"
        fill="var(--cb-accent)"
        style={{ animation: 'corePulse 2.5s ease-in-out infinite' }}
      />
      {/* Planet surface detail */}
      <ellipse cx="194" cy="133" rx="6" ry="3" fill="white" opacity="0.15" />
      <ellipse
        cx="207"
        cy="145"
        rx="8"
        ry="3"
        fill="white"
        opacity="0.08"
        transform="rotate(-20 207 145)"
      />

      {/* ── Gold ring around core ── */}
      <circle
        cx="200"
        cy="140"
        r="26"
        fill="none"
        stroke="var(--cb-gold)"
        strokeWidth="1.5"
        opacity="0.6"
      />
    </svg>

    {/* ── Text ── */}
    <div style={{ textAlign: 'center' }}>
      <p
        style={{
          fontFamily: "'Orbitron', sans-serif",
          fontSize: 13,
          letterSpacing: '0.15em',
          color: 'var(--cb-text-muted)',
          margin: 0,
          textTransform: 'uppercase',
        }}
      >
        Navigating the galaxy
        <span className="page-loader-dots" />
      </p>
    </div>
  </div>
);
